import React, { useId, useMemo } from "react";
import { IntegrationConnection } from "@common/types/integrationConnection";
import { Button } from "@design-system/Button/Button";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import { InstitutionIcon } from "@design-system/InstitutionIcon/InstitutionIcon";
import { Picker } from "@design-system/Picker/Picker.lib";
import { HStack } from "@design-system/HStack/HStack";
import { VStack } from "@design-system/VStack/VStack";
import { getQuickbooksConnectionLabel } from "./quickbooksConnectionLabel";
import styles from "./AccountingMigrationModal.module.scss";

interface QuickbooksConnectionPickerProps {
    connections: IntegrationConnection[];
    selectedConnection: IntegrationConnection | null;
    isPending: boolean;
    isLoadingConnections: boolean;
    hasConnectionsError: boolean;
    onSelect: (connection: IntegrationConnection) => void;
    onBack: () => void;
    onUseAnotherSource: () => void;
    onContinue: () => void;
    onConnect: () => void;
    onReconnect: () => void;
    onRetry: () => void;
}

export const QuickbooksConnectionPicker: React.FC<
    QuickbooksConnectionPickerProps
> = ({
    connections,
    selectedConnection,
    isPending,
    isLoadingConnections,
    hasConnectionsError,
    onSelect,
    onBack,
    onUseAnotherSource,
    onContinue,
    onConnect,
    onReconnect,
    onRetry,
}) => {
    const companyLabelId = useId();
    const companyValueId = useId();
    const isSelectedConnectionBroken = Boolean(selectedConnection?.error);
    const hasConnections = connections.length > 0;
    let primaryAction = onContinue;
    let primaryActionLabel = "Continue";
    if (hasConnectionsError) {
        primaryAction = onRetry;
        primaryActionLabel = "Retry";
    } else if (!hasConnections) {
        primaryAction = onConnect;
        primaryActionLabel = "Connect QuickBooks";
    } else if (isSelectedConnectionBroken) {
        primaryAction = onReconnect;
        primaryActionLabel = "Reconnect QuickBooks";
    }
    let description = hasConnections
        ? "This entity is already connected to a QuickBooks Online company"
        : "Connect a QuickBooks company to migrate its accounting data";
    if (isPending) {
        description = "Connecting to QuickBooks…";
    } else if (isLoadingConnections) {
        description = "Loading QuickBooks connections…";
    } else if (hasConnectionsError) {
        description = "Couldn't load QuickBooks connections.";
    }

    const connectionById = useMemo(
        () =>
            new Map(
                connections.map((connection) => [connection.id, connection]),
            ),
        [connections],
    );

    return (
        <VStack gap={24}>
            <VStack gap={16}>
                <div className={styles.description}>{description}</div>
                <HStack
                    gap={16}
                    padding={16}
                    align="center"
                    className={styles.connectionBox}
                >
                    <InstitutionIcon institution="quickbooks" size="md" />
                    <VStack gap={4} className={styles.connectionContent}>
                        {connections.length > 1 ||
                        (hasConnections && !selectedConnection) ? (
                            <>
                                <span
                                    id={companyLabelId}
                                    className={styles.description}
                                >
                                    QuickBooks company
                                </span>
                                <Picker.Select
                                    value={selectedConnection?.id ?? null}
                                    onValueChange={(connectionId) => {
                                        if (connectionId === null) {
                                            return;
                                        }
                                        const connection =
                                            connectionById.get(connectionId);
                                        if (connection) {
                                            onSelect(connection);
                                        }
                                    }}
                                    disabled={isPending || isLoadingConnections}
                                >
                                    <Picker.ButtonTrigger
                                        render={
                                            <Picker.Button
                                                block
                                                variant="primary"
                                                aria-labelledby={`${companyLabelId} ${companyValueId}`}
                                            />
                                        }
                                        value={
                                            <span id={companyValueId}>
                                                {selectedConnection
                                                    ? getQuickbooksConnectionLabel(
                                                          selectedConnection,
                                                      )
                                                    : "Select a company"}
                                            </span>
                                        }
                                    />
                                    <Picker.Popup sideOffset={4}>
                                        <Picker.List>
                                            {connections.map((connection) => (
                                                <Picker.Item
                                                    key={connection.id}
                                                    value={connection.id}
                                                    leading={
                                                        <InstitutionIcon
                                                            institution="quickbooks"
                                                            size="xs"
                                                        />
                                                    }
                                                >
                                                    {getQuickbooksConnectionLabel(
                                                        connection,
                                                    )}
                                                </Picker.Item>
                                            ))}
                                        </Picker.List>
                                    </Picker.Popup>
                                </Picker.Select>
                            </>
                        ) : (
                            <div className={styles.connectionLabel}>
                                {selectedConnection
                                    ? getQuickbooksConnectionLabel(
                                          selectedConnection,
                                      )
                                    : "QuickBooks Online"}
                            </div>
                        )}
                        {isSelectedConnectionBroken && (
                            <div className={styles.connectionNote}>
                                This connection stopped working. Reconnect
                                QuickBooks to migrate from it.
                            </div>
                        )}
                    </VStack>
                    {hasConnections && (
                        <Button
                            size="sm"
                            variant="secondary"
                            onClick={onConnect}
                            disabled={isPending || isLoadingConnections}
                        >
                            Change
                        </Button>
                    )}
                </HStack>
            </VStack>
            <HStack gap={8} justify="between" align="center" wrap>
                <Button
                    size="md"
                    variant="secondary"
                    onClick={onBack}
                    disabled={isPending}
                >
                    Back
                </Button>
                <HStack gap={8} wrap>
                    <Button
                        size="md"
                        variant="secondary"
                        onClick={onUseAnotherSource}
                        disabled={isPending}
                    >
                        Use another source
                    </Button>
                    <ButtonWithLoader
                        size="md"
                        variant="primary"
                        onClick={primaryAction}
                        loading={isPending || isLoadingConnections}
                        disabled={
                            isPending ||
                            isLoadingConnections ||
                            (hasConnections && !selectedConnection)
                        }
                    >
                        {primaryActionLabel}
                    </ButtonWithLoader>
                </HStack>
            </HStack>
        </VStack>
    );
};
